import {gql} from '@apollo/client';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {EntrySummary} from 'notebook/ELNConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import * as Headers from 'ui-library/Headers';
import * as WorkflowActions from 'workflows/WorkflowActions';

import {FullfillmentLinkQuery, FullfillmentLinkQueryVariables} from './__generated/FullfillmentLinkQuery';

interface ConnectedProps {
  entrySummaries: Array<EntrySummary> | null;
  workflowIds: Array<string>;
}

export const FULLFILLMENT_LINK_QUERY = gql`
  query FullfillmentLinkQuery($workflowIds: [StrictString!]!) @skipAudit @engComponent(name: REQUESTS) {
    workflows(ids: $workflowIds) {
      ... on WorkflowNode {
        id
        name
        humanId
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function FulfillmentLinks({entrySummaries, workflowIds}: ConnectedProps): JSX.Element | null {
  const workflowSummariesResult = useBenchlingQuery<FullfillmentLinkQuery, FullfillmentLinkQueryVariables>(
    FULLFILLMENT_LINK_QUERY,
    {variables: {workflowIds}}
  );

  if (!entrySummaries || workflowSummariesResult.type !== QueryResultType.SUCCESS) {
    return null;
  }

  const workflowSummaries = assertAllAccessible(workflowSummariesResult.data.workflows);

  return (
    <div>
      {workflowSummaries.length > 0 && (
        <div className="u-spacing-top">
          <Headers.Title>Workflows</Headers.Title>
          <div>
            {workflowSummaries.map(({id: workflowId, name, humanId}, i) => (
              <span key={workflowId}>
                {i > 0 && ', '}
                <a
                  onClick={() => {
                    WorkflowActions.routeToWorkflow(workflowId);
                  }}
                >{`${humanId} \u2022 ${name}`}</a>
              </span>
            ))}
          </div>
        </div>
      )}
      {entrySummaries.length > 0 && (
        <div className="u-spacing-top">
          <Headers.Title>Entries</Headers.Title>
          <div>
            {entrySummaries.map(({id: entryId, name}, i) => (
              <span key={entryId}>
                {i > 0 && ', '}
                <a
                  onClick={() => {
                    FileBrowserActions.openFile(entryId);
                  }}
                >
                  {name}
                </a>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function mapStateToProps(state: BenchlingState, {requestId}: {requestId: string}): ConnectedProps {
  const {fulfillmentsById, fulfillmentIdsByRequestId} = state.models.requests.fulfillments;
  const {entrySummariesById} = state.models;

  const fulfillments = fulfillmentIdsByRequestId[requestId].map((id) => fulfillmentsById[id]);
  // eslint-disable-next-line no-restricted-syntax
  const entryIds = _.uniq(_.compact(_.pluck(fulfillments, 'entryId')));
  // eslint-disable-next-line no-restricted-syntax
  const workflowIds = _.uniq(_.compact(_.pluck(fulfillments, 'workflowId')));

  if (entryIds.some((id) => entrySummariesById[id] == null)) {
    return {entrySummaries: null, workflowIds};
  }

  return {
    entrySummaries: entryIds.map((entryId) => entrySummariesById[entryId]),
    workflowIds,
  };
}

export default connect(mapStateToProps)(FulfillmentLinks);
